import React, { useCallback, useContext, useState } from 'react';
import type {
	DashboardOperationData,
	DashboardOperationResult,
} from '@jira/global-pages__directories-common-directory-v2/src/model/dashboard-operations/index.tsx';
import CreateDashboardModal from '@jira/global-pages__directories-dashboards-directory-v2-create-dashboard-modal/src/async.tsx';
import { L1_MENU_ID } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { CommonNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/common-nav-menu-button/main.tsx';
import { HideL1MenuItemSection } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/hide-l1-menu-item-action/index.tsx';
import { MoreNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import { useRouteByName } from '@jira/platform__utils-router/src/index.tsx';
import { useSwitchModals } from '@jira/platform__controllers-switch-modals/src/index.tsx';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import MenuGroup from '@atlaskit/menu/menu-group';
import AddIcon from '@atlaskit/icon/core/add';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useRouterActions, generatePath } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { CREATE_DASHBOARD, MEATBALL_DROPDOWN } from '../../constants.tsx';
import { AnalyticContext } from '../../controllers/analytic-context/index.tsx';

const createDashboard = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-dashboards.common.action-buttons.create-dashboard',

	defaultMessage: 'Create dashboard',
	description:
		'Used as the alt text for an icon button that when used navigates to create dashboard',
});

const dashboards = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-dashboards.common.action-buttons.dashboards',

	defaultMessage: 'Dashboards',
	description:
		'Name of the Dashboards section in the sidebar. Used for accessibility labels and tooltips.',
});

export function ActionButtons(): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [, { off: modalClose }] = useSwitchModals('DASHBOARD_CREATE');
	const [isOpen, setIsOpen] = useState(false);
	const { path } = useRouteByName('dashboard-edit') ?? {};
	const { push } = useRouterActions();
	const onSuccess = useCallback(
		(_: DashboardOperationData, { id }: DashboardOperationResult) => {
			path && push(generatePath(path, { dashboardId: id }));
			setIsOpen(false);
			modalClose();
		},
		[modalClose, path, push],
	);

	const { fireUIAnalyticForFixButton } = useContext(AnalyticContext);

	const onCancelCreateDashboardModal = useCallback(() => {
		setIsOpen(false);
		modalClose();
	}, [modalClose]);

	const onClickCommonNavMenuButton = useCallback(() => {
		fireUIAnalyticForFixButton({ itemId: CREATE_DASHBOARD });
		setIsOpen(true);
	}, [fireUIAnalyticForFixButton]);

	const onOpenMoreNavMenuButton = useCallback(
		() => fireUIAnalyticForFixButton({ itemId: MEATBALL_DROPDOWN }),
		[fireUIAnalyticForFixButton],
	);
	return (
		<>
			{isOpen && (
				<CreateDashboardModal onSuccess={onSuccess} onCancel={onCancelCreateDashboardModal} />
			)}
			<Tooltip content={formatMessage(createDashboard)}>
				<CommonNavMenuButton
					interactionName="nav4-sidebar-dashboards-create-button"
					icon={(iconProps) => <AddIcon {...iconProps} size="small" />}
					label={formatMessage(createDashboard)}
					onClick={onClickCommonNavMenuButton}
				/>
			</Tooltip>
			{!fg('navx-global-side-nav') && (
				<MoreNavMenuButton
					onOpen={onOpenMoreNavMenuButton}
					MenuItems={MenuItems}
					name={formatMessage(dashboards)}
				/>
			)}
		</>
	);
}

const MenuItems = () => (
	<MenuGroup>
		<HideL1MenuItemSection menuId={L1_MENU_ID.DASHBOARDS} hasSeparator={false} />
	</MenuGroup>
);
